import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { TokenService } from './token.service';
import {
  AuthSuccessResponse,
  LoginCredentialsDTO,
  UserDTO,
  AuthErrorResponse,
} from '../dtos/auth.dto';
import { Router } from '@angular/router';
import { environment } from 'environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly API_URL = `${environment.apiUrl}/v1/auth`;
  private currentUserSubject = new BehaviorSubject<UserDTO | null>(null);
  currentUser$ = this.currentUserSubject.asObservable();

  constructor(
    private http: HttpClient,
    private tokenService: TokenService,
    private router: Router,
  ) {
    this.loadStoredUser();
  }

  private loadStoredUser(): void {
    const storedUser = this.tokenService.getUser();
    if (storedUser) {
      this.currentUserSubject.next(JSON.parse(storedUser));
    }
  }

  login(
    credentials: LoginCredentialsDTO,
    rememberMe: boolean,
  ): Observable<{
    status: number;
    response: AuthSuccessResponse | AuthErrorResponse;
  }> {
    const params = new HttpParams().set('ignorePrefix', 'true');

    return this.http
      .post<{ response: AuthSuccessResponse | AuthErrorResponse }>(
        `${this.API_URL}/login-no-code`,
        credentials,
        {
          params,
          observe: 'response',
        },
      )
      .pipe(
        map(
          (
            response: HttpResponse<{
              response: AuthSuccessResponse | AuthErrorResponse;
            }>,
          ) => {
            return {
              status: response.status,
              response: response.body?.response as
                | AuthSuccessResponse
                | AuthErrorResponse,
            };
          },
        ),
        tap(({ status, response }) => {
          if (status === 200) {
            const successResponse = response as AuthSuccessResponse;
            const user: UserDTO = {
              fullName: successResponse.fullName,
              email: successResponse.email,
              cpf: successResponse.cpf,
              birthDate: successResponse.birthDate,
              phone: successResponse.phone,
              roles: successResponse.roles,
            };

            this.tokenService.setRememberMe(rememberMe);
            this.tokenService.setToken(successResponse.accessToken);
            this.tokenService.setUser(JSON.stringify(user));
            this.currentUserSubject.next(user);
          }
        }),
        catchError(error => {
          if (error.status === 404) {
            return throwError(
              () =>
                new Error(
                  'O usuário especificado não foi encontrado na base de dados.',
                ),
            );
          }
          return throwError(
            () =>
              new Error(
                'Ocorreu um erro ao tentar fazer login. Por favor, tente novamente.',
              ),
          );
        }),
      );
  }

  logout(): void {
    this.tokenService.clear();
    this.currentUserSubject.next(null);
    this.router.navigate(['/login']);
  }

  isAuthenticated(): boolean {
    return this.tokenService.hasToken();
  }

  getCurrentUser(): UserDTO | null {
    return this.currentUserSubject.value;
  }
}
